import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import styled from 'styled-components';
import {
  FaVideo,
  FaPlay,
  FaYoutube,
  FaSpinner,
  FaFilter,
  FaSearch
} from 'react-icons/fa';

const Container = styled.div`
  padding: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
  background: #f8f9fa;
  min-height: 100vh;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const Title = styled.h1`
  color: #292929;
  margin: 0;
  font-size: 1.8rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: #6c63ff;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  
  @media (max-width: 768px) {
    width: 100%;
    flex-wrap: wrap;
  }
`;

const SearchInput = styled.div`
  display: flex;
  align-items: center;
  background: #f1f3f4;
  border-radius: 24px;
  padding: 0.5rem 1rem;
  
  svg {
    color: #666;
    margin-right: 0.5rem;
  }
  
  input {
    border: none;
    background: transparent;
    outline: none;
    font-size: 1rem;
    color: #333;
    
    &::placeholder {
      color: #999;
    }
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FilterButton = styled.button`
  background: ${props => props.active ? '#6c63ff' : 'white'};
  color: ${props => props.active ? 'white' : '#666'};
  border: 1px solid ${props => props.active ? '#6c63ff' : '#e1e5e9'};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  
  &:hover {
    background: ${props => props.active ? '#5753e6' : '#f8f9fa'};
  }
`;

const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const VideoCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  }
`;

const VideoThumbnail = styled.div`
  height: 180px;
  background: #eee;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.3);
    opacity: 0;
    transition: opacity 0.3s;
  }
  
  &:hover:before {
    opacity: 1;
  }
`;

const CategoryLabel = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(108, 99, 255, 0.9);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: capitalize;
`;

const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  background: rgba(255,255,255,0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  transition: all 0.3s;
  
  svg {
    color: #f00;
    font-size: 24px;
  }
  
  &:hover {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.1);
  }
`;

const VideoInfo = styled.div`
  padding: 1.25rem;
`;

const VideoTitle = styled.h4`
  margin: 0 0 0.5rem 0;
  color: #333;
  font-size: 1.1rem;
`;

const VideoDescription = styled.p`
  margin: 0;
  color: #666;
  font-size: 0.9rem;
  line-height: 1.5;
  
  &.truncated {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const VideoMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1.25rem;
  border-top: 1px solid #eee;
  background: #f9fafb;
  color: #666;
  font-size: 0.9rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
`;

const LoadingDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  color: #666;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  width: 90%;
  max-width: 1000px;
  max-height: 90vh;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: -40px;
  right: 0;
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 1001;
  
  &:hover {
    color: #f00;
  }
`;

const VideoPlayer = styled.div`
  position: relative;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;
  border-radius: 8px;
  
  iframe, video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const ClientVideos = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  
  useEffect(() => {
    if (currentUser) {
      loadVideos();
    } else {
      navigate('/login');
    }
  }, [currentUser]);
  
  const loadVideos = async () => {
    try {
      setLoading(true);
      
      // Get the user document to check for shared videos
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        setError('User data not found');
        setLoading(false);
        return;
      }
      
      const userData = userDoc.data();
      const sharedVideoIds = userData.sharedVideos || [];
      
      if (sharedVideoIds.length === 0) {
        setVideos([]);
        setLoading(false);
        return;
      }
      
      // Get all videos that are shared with this client
      const videosList = [];
      
      // Fetch videos in batches to avoid large queries
      const batchSize = 10;
      for (let i = 0; i < sharedVideoIds.length; i += batchSize) {
        const batch = sharedVideoIds.slice(i, i + batchSize);
        const videosRef = collection(db, 'videos');
        const q = query(videosRef, where('__name__', 'in', batch));
        const querySnapshot = await getDocs(q);
        
        querySnapshot.forEach((doc) => {
          videosList.push({
            id: doc.id,
            ...doc.data()
          });
        });
      }
      
      // Sort videos by creation date (newest first)
      videosList.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return dateB - dateA;
      });
      
      setVideos(videosList);
      
    } catch (err) {
      console.error('Error loading videos:', err);
      setError('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };
  
  const handleVideoClick = (video) => {
    setCurrentVideo(video);
    setShowVideoModal(true);
  };
  
  const handleCategoryClick = (category) => {
    setActiveCategory(category);
  };
  
  const getCategoryColor = (category) => {
    const colors = {
      workout: '#28a745',
      nutrition: '#fd7e14',
      education: '#007bff',
      motivation: '#6f42c1',
      technique: '#17a2b8',
      other: '#6c757d'
    };
    return colors[category] || '#6c63ff';
  };
  
  // Filter videos by search and category
  const filteredVideos = videos.filter(video => {
    // Filter by search term
    const matchesSearch = !searchTerm || 
      video.title?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      video.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by category
    const matchesCategory = activeCategory === 'all' || video.category === activeCategory;
    
    return matchesSearch && matchesCategory;
  });
  
  // Extract available categories from videos
  const categories = ['all', ...new Set(videos.map(video => video.category))].filter(Boolean);
  
  // Handle embedded video URL
  const getEmbedUrl = (url) => {
    if (!url) return '';
    
    // Handle YouTube links
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = extractYoutubeId(url);
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }
    
    // Handle Vimeo links
    if (url.includes('vimeo.com')) {
      const vimeoId = url.match(/vimeo\.com\/(\d+)/);
      if (vimeoId && vimeoId[1]) {
        return `https://player.vimeo.com/video/${vimeoId[1]}`;
      }
    }
    
    // Return original URL for other cases
    return url;
  };
  
  const extractYoutubeId = (url) => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : null;
  };
  
  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };
  
  return (
    <Container>
      <Header>
        <Title>
          <FaVideo /> Your Videos
        </Title>
        <FilterContainer>
          <SearchInput>
            <FaSearch />
            <input 
              type="text" 
              placeholder="Search videos..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchInput>
          
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {categories.map(category => (
              <FilterButton 
                key={category} 
                active={activeCategory === category}
                onClick={() => handleCategoryClick(category)}
                style={{ 
                  background: activeCategory === category ? getCategoryColor(category) : 'white',
                  borderColor: activeCategory === category ? getCategoryColor(category) : '#e1e5e9'
                }}
              >
                <FaFilter /> {category.charAt(0).toUpperCase() + category.slice(1)}
              </FilterButton>
            ))}
          </div>
        </FilterContainer>
      </Header>
      
      {loading ? (
        <LoadingDiv>
          <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '2rem', marginRight: '1rem' }} />
          Loading videos...
        </LoadingDiv>
      ) : error ? (
        <EmptyState>
          <h3>Error loading videos</h3>
          <p>{error}</p>
        </EmptyState>
      ) : videos.length === 0 ? (
        <EmptyState>
          <div style={{ fontSize: '5rem', color: '#ddd', marginBottom: '1rem' }}>
            <FaVideo />
          </div>
          <h3>No Videos Available</h3>
          <p>Your fitness partner hasn't shared any videos with you yet.</p>
        </EmptyState>
      ) : filteredVideos.length === 0 ? (
        <EmptyState>
          <div style={{ fontSize: '3rem', color: '#ddd', marginBottom: '1rem' }}>
            <FaSearch />
          </div>
          <h3>No Matching Videos</h3>
          <p>Try changing your search terms or filter.</p>
        </EmptyState>
      ) : (
        <VideoGrid>
          {filteredVideos.map(video => (
            <VideoCard key={video.id}>
              <VideoThumbnail onClick={() => handleVideoClick(video)}>
                <img 
                  src={video.thumbnailUrl || 'https://placehold.co/300x180?text=Video+Thumbnail'} 
                  alt={video.title} 
                />
                <PlayButton>
                  <FaPlay />
                </PlayButton>
                {video.category && (
                  <CategoryLabel style={{ background: getCategoryColor(video.category) }}>
                    {video.category}
                  </CategoryLabel>
                )}
              </VideoThumbnail>
              <VideoInfo>
                <VideoTitle>{video.title}</VideoTitle>
                <VideoDescription className="truncated">
                  {video.description || 'No description available.'}
                </VideoDescription>
              </VideoInfo>
              <VideoMeta>
                <span>Added: {formatDate(video.createdAt)}</span>
                <span>By: {video.partnerName}</span>
              </VideoMeta>
            </VideoCard>
          ))}
        </VideoGrid>
      )}
      
      {/* Video Player Modal */}
      {showVideoModal && currentVideo && (
        <Modal onClick={() => setShowVideoModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setShowVideoModal(false)}>✕</CloseButton>
            <VideoPlayer>
              {currentVideo.type === 'external' && currentVideo.videoUrl ? (
                <iframe 
                  src={getEmbedUrl(currentVideo.videoUrl)} 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen
                  title={currentVideo.title}
                ></iframe>
              ) : (
                <video controls src={currentVideo.videoUrl}>
                  Your browser does not support the video tag.
                </video>
              )}
            </VideoPlayer>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default ClientVideos; 